import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Block, Button, Icon, Preloader } from "framework7-react";
import { useNow } from "../../hooks/use-now";
import { useToast } from "../../hooks/use-toast";
import { createTransferCode } from "../../redux/reducer/session-transfer";
import {
  formatRemaining,
  formatTransferCode,
  remainingSeconds,
  transferDeadline,
} from "../../utils/transfer-code";

const TransferCode = ({ active }) => {
  const dispatch = useDispatch();
  const { showInfo, showError } = useToast();
  const [transfer, setTransfer] = useState({ status: "loading" });
  const latestRequest = useRef(0);

  const create = useCallback(() => {
    const request = ++latestRequest.current;
    const isLatest = () => request === latestRequest.current;
    setTransfer({ status: "loading" });
    dispatch(
      createTransferCode(
        ({ code, expiresIn }) => {
          if (!isLatest()) return;
          const createdAt = Date.now();
          setTransfer({
            status: "ready",
            code,
            createdAt,
            deadline: transferDeadline(expiresIn, createdAt),
          });
        },
        (message) => {
          if (isLatest()) setTransfer({ status: "error", message });
        },
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (active) create();
  }, [active, create]);

  const isReady = transfer.status === "ready";
  const now = useNow(active && isReady);
  const seconds = isReady
    ? remainingSeconds(transfer.deadline, Math.max(now, transfer.createdAt))
    : 0;
  const status = isReady && seconds === 0 ? "expired" : transfer.status;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(transfer.code);
      showInfo("Code kopiert");
    } catch (error) {
      showError("Kopieren nicht möglich");
    }
  };

  return (
    <Block className={`transfer-code transfer-code-${status}`}>
      {status === "loading" && <Preloader />}
      {status === "ready" && (
        <>
          <Button
            tonal
            className="hash-highlight transfer-code-button"
            onClick={copyCode}
          >
            <span className="hash-highlight-code transfer-code-value">
              {formatTransferCode(transfer.code)}
            </span>
            <Icon ios="f7:rectangle_on_rectangle" md="material:content_copy" />
          </Button>
          <div className="transfer-code-validity">
            Noch {formatRemaining(seconds)} Min. gültig
          </div>
        </>
      )}
      {(status === "expired" || status === "error") && (
        <>
          <div className="transfer-code-message">
            {status === "expired"
              ? "Der Code ist abgelaufen."
              : transfer.message}
          </div>
          <Button tonal onClick={create}>
            {status === "expired" ? "Neuen Code erstellen" : "Erneut versuchen"}
          </Button>
        </>
      )}
    </Block>
  );
};

export default TransferCode;
